import React, { useState } from 'react';
import { Modal, Button, DateInput, FormActions } from './ui';

const heuteIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmt = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/**
 * Liegt die Unterschrift vor heute, wird gefragt, ab wann abgerechnet wird – bei jedem Vertrag, nicht
 * erst ab einem Mindestabstand: auch ein Vertrag von letzter Woche kann schon anders abgerechnet sein.
 */
export const brauchtRueckwirkendFrage = (unterschriftsdatum: string, hatPositionen: boolean): boolean =>
  hatPositionen && !!unterschriftsdatum && unterschriftsdatum < heuteIso();

/**
 * Nachfrage beim Anlegen eines Vertrags, dessen Unterschrift vor heute liegt (z. B. ein Altvertrag
 * aus der Zeit vor dem Portal): erst ab jetzt abrechnen – die erste Rechnung kommt zum nächsten
 * Jahrestag – oder rückwirkend ab einem Datum, dann entsteht für jedes Vertragsjahr seitdem ein
 * eigener Entwurf. Gibt das gewählte „Abrechnung ab“-Datum zurück.
 */
const RueckwirkendDialog: React.FC<{
  unterschriftsdatum: string;
  saving: boolean;
  onWahl: (abrechnungAb: string) => void;
  onAbbrechen: () => void;
}> = ({ unterschriftsdatum, saving, onWahl, onAbbrechen }) => {
  const [ab, setAb] = useState(unterschriftsdatum);

  return (
    <Modal title="Rückwirkend abrechnen?" onClose={onAbbrechen}>
      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          Der Vertrag wurde am <strong>{fmt(unterschriftsdatum)}</strong> unterschrieben, also vor heute. Sollen
          Rechnungen rückwirkend erzeugt werden, oder wird erst ab jetzt abgerechnet?
        </p>
        <div className="rounded-lg border border-brand-300 bg-brand-50/40 px-4 py-3">
          <p className="text-sm font-medium text-gray-900">Rückwirkend abrechnen</p>
          <p className="text-xs text-gray-500">
            Für jedes Vertragsjahr ab dem gewählten Datum bis heute entsteht ein eigener Rechnungsentwurf –
            ab Unterschrift ist das die normale Erstrechnung (bei älteren Verträgen dazu jedes Folgejahr).
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-500">Abrechnen ab
              <DateInput value={ab} onChange={setAb} />
            </label>
            <Button type="button" loading={saving} disabled={!ab} onClick={() => onWahl(ab)}>
              Rückwirkend anlegen
            </Button>
          </div>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={() => onWahl(heuteIso())}
          className="w-full rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50 disabled:opacity-50"
        >
          <p className="text-sm font-medium text-gray-900">Erst ab jetzt abrechnen</p>
          <p className="text-xs text-gray-500">
            Alles bis heute gilt als erledigt – <strong>auch Grundpacht und erstes Vertragsjahr werden nicht
            berechnet</strong>. Die erste Rechnung kommt automatisch zum nächsten Jahrestag der Unterschrift.
          </p>
        </button>
        <FormActions>
          <Button type="button" variant="secondary" disabled={saving} onClick={onAbbrechen}>Zurück</Button>
        </FormActions>
      </div>
    </Modal>
  );
};

export default RueckwirkendDialog;
